/**
 * Natural Language Rule Parser
 *
 * Converts plain-English rule descriptions into Rule objects.
 * Local regex engine handles common patterns; falls back to Claude API
 * for complex inputs.
 */

import type { Action, BetSide, Modifiers, ProgressionMethod, Rule, Trigger } from "../types";
import { askAgent } from "../agent/agentApi";

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

const WORD_NUMS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  a: 1,
  an: 1,
};

/** Parse a number that may be digits or a word ("3" or "three"). An optional
 * regex group that did not match arrives as undefined. */
function parseNum(s: string | undefined): number | undefined {
  if (s === undefined) return undefined;
  const trimmed = s.trim().toLowerCase();
  if (WORD_NUMS[trimmed] !== undefined) return WORD_NUMS[trimmed];
  const n = Number.parseFloat(trimmed);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * Match a number token in text — digits or English word.
 * Returns a regex source string.
 */
const NUM_RE = String.raw`(\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|a(?:n)?)`;

function parseSide(s: string): BetSide | "Any" {
  const u = s.toLowerCase();
  if (u.includes("banker") || u === "b") return "Banker";
  if (u.includes("player") || u === "p") return "Player";
  if (u.includes("tie") || u === "t") return "Tie";
  return "Any";
}

function parseProgression(s: string): ProgressionMethod {
  const u = s.toLowerCase();
  if (u.includes("martingale") || u.includes("double")) return "martingale";
  if (u.includes("fibonacci") || u.includes("fib")) return "fibonacci";
  if (u.includes("d'alembert") || u.includes("dalembert") || u.includes("alembert"))
    return "dalembert";
  if (u.includes("labouchere") || u.includes("labo")) return "labouchere";
  if (u.includes("oscar") || u.includes("grind")) return "oscars_grind";
  if (u.includes("1-3-2-6") || u.includes("1326")) return "1326";
  return "flat";
}

/** "1 loss", "3 losses", "1 win", "2 wins". */
function resultWord(count: number, isLoss: boolean): string {
  if (isLoss) return count > 1 ? "losses" : "loss";
  return count > 1 ? "wins" : "win";
}

function makeRule(partial: {
  label: string;
  trigger: Trigger;
  action: Action;
  modifiers?: Modifiers;
}): Rule {
  return {
    id: uid(),
    priority: 0,
    enabled: true,
    modifiers: { shoe_reset: "reset" },
    ...partial,
  };
}

// ────────────────────────────────────────────────────────────
// Pattern registry — each returns 0, 1 or multiple Rules
// ────────────────────────────────────────────────────────────

type PatternFn = (text: string) => Rule[] | null;

// "double the bet", "3x stake", "1.5 wager", then "after 2 losses". Split in
// two so neither pattern backtracks: the lookbehind keeps a match from
// starting inside a number, and the sticky second pattern must follow the
// first directly.
const MULTIPLIER_BET = /(?<![\d.])(double|triple|(\d+(?:\.\d+)?)x?)\s+(?:the\s+)?(?:bet|wager|stake)\s+/gi;
const AFTER_RESULTS = /(?:after|when|on)\s+(?:every\s+)?(\d+)\s*(loss(?:es)?|wins?)/iy;

function matchMultiplier(t: string) {
  for (const bet of t.matchAll(MULTIPLIER_BET)) {
    AFTER_RESULTS.lastIndex = (bet.index ?? 0) + bet[0].length;
    const after = AFTER_RESULTS.exec(t);
    if (after) return { word: bet[1], number: bet[2], count: after[1], result: after[2] };
  }
  return null;
}

function multiplierValue(word: string, number: string | undefined): number {
  if (number) return Number.parseFloat(number);
  return word.toLowerCase() === "triple" ? 3 : 2;
}

const patterns: PatternFn[] = [
  // ── Stop loss ──────────────────────────────────────────────
  (t) => {
    const m = new RegExp(
      String.raw`stop\s*(?:loss|when\s+(?:loss|down|losing)(?:\s+(?:exceeds?|more\s+than|over))?)[\s$]*${NUM_RE}`,
      "i"
    ).exec(t);
    if (!m) return null;
    const amt = parseNum(m[1])!;
    return [
      makeRule({
        label: `Stop Loss at $${amt}`,
        trigger: { type: "financial_state", condition: "session_loss", threshold: -amt },
        action: { type: "stop_loss", threshold: -amt },
        modifiers: { shoe_reset: "carry" },
      }),
    ];
  },

  // ── Take profit ────────────────────────────────────────────
  (t) => {
    const m =
      new RegExp(String.raw`take\s*profit(?:\s+(?:at|when(?:\s+up)?))? [\s$]*${NUM_RE}`, "i").exec(t) ??
      new RegExp(
        String.raw`(?:quit|stop)\s+when\s+(?:up|profit|winning)\s*(?:over|exceeds?|more\s+than)?\s*\$?${NUM_RE}`,
        "i"
      ).exec(t);
    if (!m) return null;
    const amt = parseNum(m[1])!;
    return [
      makeRule({
        label: `Take Profit at $${amt}`,
        trigger: { type: "financial_state", condition: "session_profit", threshold: amt },
        action: { type: "take_profit", threshold: amt },
        modifiers: { shoe_reset: "carry" },
      }),
    ];
  },

  // ── Compound: increase/raise bet N units after N SIDE wins, then reset ──
  // "after 3 bankers increase bet 3 units then back to base"
  // "after 3 banker wins raise the bet 2 units then reset after a win"
  (t) => {
    // The "then back to base / reset" tail is read from the text below, so
    // the pattern stops at the unit count.
    const m = new RegExp(
      String.raw`after\s+${NUM_RE}\s+(\w+)\s+(?:consecutive\s+)?(?:wins?|in\s+a\s+row)?\s*,?\s*` +
        String.raw`(?:increase|raise|add|bump|boost)\s+(?:the\s+)?(?:bet|wager|stake|unit)s?\s+(?:by\s+)?${NUM_RE}`,
      "i"
    ).exec(t);
    if (!m) return null;

    const streakCount = parseNum(m[1]);
    const side = parseSide(m[2]);
    const addUnits = parseNum(m[3]);
    if (!streakCount || !addUnits) return null;

    const streakSide = side === "Any" ? "Banker" : (side as BetSide);
    const hasResetClause = /then\s+(?:back|reset)/i.test(t);

    const rules: Rule[] = [
      makeRule({
        label: `Bet +${addUnits}u on ${streakSide} after ${streakCount} wins`,
        trigger: {
          type: "streak",
          side: streakSide,
          direction: "consecutive_wins",
          min_length: streakCount,
        },
        action: { type: "place_bet", side: streakSide, unit_size: addUnits },
      }),
    ];

    if (hasResetClause) {
      // Determine reset condition — look for "after a win/loss" in the tail
      const tail = t.slice(t.toLowerCase().indexOf("then"));
      const resetOnLoss = /loss|losing|lose/i.test(tail);
      rules.push(
        makeRule({
          label: `Reset to base after ${resetOnLoss ? "loss" : "win"}`,
          trigger: {
            type: "streak",
            side: streakSide,
            direction: resetOnLoss ? "consecutive_losses" : "consecutive_wins",
            min_length: 1,
          },
          action: { type: "reset_progression", reset_to: 1 },
          modifiers: { shoe_reset: "carry" },
        })
      );
    }

    return rules;
  },

  // ── Increase/raise bet after N losses/wins (without side, simpler) ──
  // "increase bet 2 units after 3 losses then reset"
  (t) => {
    const m = new RegExp(
      String.raw`(?:increase|raise|add|bump)\s+(?:the\s+)?(?:bet|wager|stake|unit)s?\s+(?:by\s+)?${NUM_RE}\s*(?:units?)?\s*` +
        String.raw`(?:after|when|on)\s+${NUM_RE}\s+(?:consecutive\s+)?(loss(?:es)?|wins?)`,
      "i"
    ).exec(t);
    if (!m) return null;

    const addUnits = parseNum(m[1]);
    const count = parseNum(m[2]);
    const isLoss = /loss/i.test(m[3]);
    if (!addUnits || !count) return null;

    const hasReset = /then\s+(?:back|reset)/i.test(t);
    const rules: Rule[] = [
      makeRule({
        label: `Add ${addUnits}u after ${count} ${resultWord(count, isLoss)}`,
        trigger: {
          type: "streak",
          side: "Any",
          direction: isLoss ? "consecutive_losses" : "consecutive_wins",
          min_length: count,
        },
        action: { type: "adjust_unit", method: "add", value: addUnits },
      }),
    ];
    if (hasReset) {
      rules.push(
        makeRule({
          label: `Reset to base after ${isLoss ? "win" : "loss"}`,
          trigger: {
            type: "streak",
            side: "Any",
            direction: isLoss ? "consecutive_wins" : "consecutive_losses",
            min_length: 1,
          },
          action: { type: "reset_progression", reset_to: 1 },
          modifiers: { shoe_reset: "carry" },
        })
      );
    }
    return rules;
  },

  // ── Skip N hands after tie / when ... ─────────────────────
  (t) => {
    const m =
      new RegExp(String.raw`skip\s+${NUM_RE}\s+hands?\s+(?:after|when|on|if)\s+(.+)`, "i").exec(t) ??
      new RegExp(String.raw`(?:after|when|on)\s+(?:a\s+)?tie[,\s]+skip\s+${NUM_RE}`, "i").exec(t);
    if (!m) return null;
    if (m.length >= 3 && parseNum(m[1]) !== undefined) {
      const count = parseNum(m[1])!;
      const context = m[2];
      const isAfterTie = /tie/i.test(context);
      return [
        makeRule({
          label: `Skip ${count} hand${count > 1 ? "s" : ""} after ${isAfterTie ? "Tie" : context.trim()}`,
          trigger: isAfterTie
            ? { type: "streak", side: "Tie", direction: "consecutive_wins", min_length: 1 }
            : { type: "hand_count", hand_min: 1 },
          action: { type: "skip_hand", skip_count: count },
        }),
      ];
    }
    const count = parseNum(m[1])!;
    return [
      makeRule({
        label: `Skip ${count} hand${count > 1 ? "s" : ""} after Tie`,
        trigger: { type: "streak", side: "Tie", direction: "consecutive_wins", min_length: 1 },
        action: { type: "skip_hand", skip_count: count },
      }),
    ];
  },

  // ── Bet on SIDE when bankroll below/above $N ───────────────
  (t) => {
    const m = new RegExp(
      String.raw`(?:bet|wager|place|play)\s+(?:on\s+)?(\w+)\s+when\s+(?:bankroll|balance)\s+(below|above|under|over)\s*\$?${NUM_RE}`,
      "i"
    ).exec(t);
    if (!m) return null;
    const side = parseSide(m[1]);
    const isBelow = /below|under/i.test(m[2]);
    const amt = parseNum(m[3])!;
    return [
      makeRule({
        label: `Bet ${side} when bankroll ${isBelow ? "below" : "above"} $${amt}`,
        trigger: {
          type: "financial_state",
          condition: isBelow ? "bankroll_below" : "bankroll_above",
          threshold: amt,
        },
        action: { type: "place_bet", side: side === "Any" ? "Banker" : (side as BetSide) },
      }),
    ];
  },

  // ── Use PROGRESSION after N consecutive losses/wins ────────
  (t) => {
    const m =
      new RegExp(
        String.raw`use\s+(.+?)\s+(?:progression\s+)?(?:after|when|on)\s+${NUM_RE}\s+consecutive\s+(loss(?:es)?|wins?)`,
        "i"
      ).exec(t) ??
      new RegExp(
        String.raw`(martingale|fibonacci|d'?alembert|labouchere|oscar'?s?\s*grind|1[- ]?3[- ]?2[- ]?6)` +
          String.raw`\s+(?:after|when|on)\s+${NUM_RE}?\s*(loss(?:es)?|wins?)`,
        "i"
      ).exec(t);
    if (!m) return null;
    const prog = parseProgression(m[1]);
    const count = parseNum(m[2]) ?? 1;
    const isLoss = /loss/i.test(m[3] ?? m[2] ?? "loss");
    return [
      makeRule({
        label: `${prog} after ${count} ${resultWord(count, isLoss)}`,
        trigger: {
          type: "streak",
          side: "Any",
          direction: isLoss ? "consecutive_losses" : "consecutive_wins",
          min_length: count,
        },
        action: { type: "adjust_unit", method: prog, value: 2 },
      }),
    ];
  },

  // ── Double / triple / Nx bet after N losses/wins ───────────
  (t) => {
    const m = matchMultiplier(t);
    if (!m) return null;
    const mult = multiplierValue(m.word, m.number);
    const count = parseNum(m.count)!;
    const isLoss = /loss/i.test(m.result);
    return [
      makeRule({
        label: `${mult}× bet after ${count} ${resultWord(count, isLoss)}`,
        trigger: {
          type: "streak",
          side: "Any",
          direction: isLoss ? "consecutive_losses" : "consecutive_wins",
          min_length: count,
        },
        action: { type: "adjust_unit", method: "multiply", value: mult },
      }),
    ];
  },

  // ── Bet N units on SIDE after N consecutive SIDE wins/losses ─
  (t) => {
    const m = new RegExp(
      String.raw`bet\s+${NUM_RE}\s*(?:units?)?\s+on\s+(\w+)\s+(?:after|when|following)\s+${NUM_RE}\s+(?:consecutive\s+)?(\w+)\s+(wins?|loss(?:es)?)`,
      "i"
    ).exec(t);
    if (!m) return null;
    const units = parseNum(m[1]);
    const betSide = parseSide(m[2]);
    const count = parseNum(m[3]);
    const streakSide = parseSide(m[4]);
    const isLoss = /loss/i.test(m[5]);
    if (!units || !count) return null;
    return [
      makeRule({
        label: `Bet ${units}u on ${betSide} after ${count} ${streakSide} ${isLoss ? "losses" : "wins"}`,
        trigger: {
          type: "streak",
          side: streakSide,
          direction: isLoss ? "consecutive_losses" : "consecutive_wins",
          min_length: count,
        },
        action: {
          type: "place_bet",
          side: betSide === "Any" ? "Banker" : (betSide as BetSide),
          unit_size: units,
        },
      }),
    ];
  },

  // ── Bet on SIDE after N SIDE wins/losses (simpler) ─────────
  (t) => {
    const m = new RegExp(
      String.raw`(?:bet|wager|play)\s+(?:on\s+)?(\w+)\s+after\s+${NUM_RE}\s+(?:consecutive\s+)?(\w+)\s+(wins?|loss(?:es)?)`,
      "i"
    ).exec(t);
    if (!m) return null;
    const betSide = parseSide(m[1]);
    if (betSide === "Any") return null;
    const count = parseNum(m[2]);
    const streakSide = parseSide(m[3]);
    const isLoss = /loss/i.test(m[4]);
    if (!count) return null;
    return [
      makeRule({
        label: `Bet ${betSide} after ${count} ${streakSide} ${isLoss ? "losses" : "wins"}`,
        trigger: {
          type: "streak",
          side: streakSide,
          direction: isLoss ? "consecutive_losses" : "consecutive_wins",
          min_length: count,
        },
        action: { type: "place_bet", side: betSide as BetSide, unit_size: 1 },
      }),
    ];
  },

  // ── Reset progression after a win/loss ─────────────────────
  (t) => {
    const m = /reset\s+(?:the\s+)?(?:progression|bet|units?)\s+(?:after|when|on)\s+(?:a\s+)?(win|loss)/i.exec(t);
    if (!m) return null;
    const isWin = /win/i.test(m[1]);
    return [
      makeRule({
        label: `Reset progression after ${isWin ? "win" : "loss"}`,
        trigger: {
          type: "streak",
          side: "Any",
          direction: isWin ? "consecutive_wins" : "consecutive_losses",
          min_length: 1,
        },
        action: { type: "reset_progression", reset_to: 1 },
        modifiers: { shoe_reset: "carry" },
      }),
    ];
  },

  // ── Always / flat bet on SIDE ───────────────────────────────
  (t) => {
    const m = /(?:always|flat(?:\s+bet)?|just|only)\s+(?:bet\s+)?(?:on\s+)?(\w+)/i.exec(t);
    if (!m) return null;
    const side = parseSide(m[1]);
    if (side === "Any") return null;
    return [
      makeRule({
        label: `Always bet ${side} (flat)`,
        trigger: { type: "hand_count", hand_min: 1 },
        action: { type: "place_bet", side: side as BetSide, unit_size: 1 },
        modifiers: { shoe_reset: "carry" },
      }),
    ];
  },
];

// ────────────────────────────────────────────────────────────
// AI fallback
// ────────────────────────────────────────────────────────────

const AI_PARSE_PROMPT = `You are a Baccarat strategy rule parser. Convert the user's plain-English description into a JSON array of rule objects. Return ONE rule object normally, but return TWO if the description contains a compound instruction like "then back to base" or "then reset".

Each rule object must match this schema exactly:
{
  "label": string,
  "trigger": {
    "type": "streak" | "pattern" | "financial_state" | "hand_count",
    "side"?: "Banker" | "Player" | "Tie" | "Any",
    "direction"?: "consecutive_wins" | "consecutive_losses" | "alternating",
    "min_length"?: number,
    "pattern"?: string,
    "lookback"?: number,
    "condition"?: "session_loss" | "session_profit" | "bankroll_below" | "bankroll_above",
    "threshold"?: number,
    "hand_min"?: number,
    "hand_max"?: number
  },
  "action": {
    "type": "place_bet" | "adjust_unit" | "skip_hand" | "reset_progression" | "stop_loss" | "take_profit",
    "side"?: "Banker" | "Player" | "Tie",
    "unit_size"?: number,
    "method"?: "flat" | "martingale" | "fibonacci" | "dalembert" | "labouchere" | "oscars_grind" | "1326" | "add" | "multiply",
    "value"?: number,
    "skip_count"?: number,
    "reset_to"?: number,
    "threshold"?: number
  },
  "modifiers": {
    "max_bet"?: number,
    "shoe_reset"?: "carry" | "reset"
  }
}

Reply with ONLY a valid JSON array, e.g. [{...}] or [{...},{...}]. No markdown, no explanation.`;

async function parseWithAI(text: string): Promise<Rule[] | null> {
  try {
    const raw = await askAgent(
      { system: AI_PARSE_PROMPT, max_tokens: 1024, messages: [{ role: "user", content: text }] },
      12_000
    );
    const json = raw
      .replace(/```(?:json)?/g, "")
      .replace(/```/g, "")
      .trim();
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) return null;
    const arr: Rule[] = parsed
      .filter((r: unknown): r is Record<string, unknown> => {
        if (typeof r !== "object" || r === null) return false;
        const obj = r as Record<string, unknown>;
        return (
          "trigger" in obj &&
          typeof obj.trigger === "object" &&
          "action" in obj &&
          typeof obj.action === "object"
        );
      })
      .map(
        (r) =>
          ({
            id: uid(),
            priority: 0,
            enabled: true,
            modifiers: { shoe_reset: "reset" as const },
            ...r,
          }) as Rule
      );
    return arr.length ? arr : null;
  } catch {
    return null;
  }
}

// ────────────────────────────────────────────────────────────
// Main export
// ────────────────────────────────────────────────────────────

export interface NLParseResult {
  rules: Rule[];
  method: "regex" | "ai" | "failed";
  confidence: "high" | "low" | "none";
}

export async function parseNLRule(text: string): Promise<NLParseResult> {
  const trimmed = text.trim();
  if (!trimmed) return { rules: [], method: "failed", confidence: "none" };

  // 1. Local patterns
  for (const fn of patterns) {
    const result = fn(trimmed);
    if (result && result.length > 0) {
      return { rules: result, method: "regex", confidence: "high" };
    }
  }

  // 2. AI fallback
  const aiRules = await parseWithAI(trimmed);
  if (aiRules) {
    return { rules: aiRules, method: "ai", confidence: "low" };
  }

  return { rules: [], method: "failed", confidence: "none" };
}
